<template>
  <QuestionPadding>
    <component
      :is="ChoiceComponent"
      :max-answers="question.maxAnswers"
      :fields="fields"
      :answers="answers"
      :selected-field-ids="selectedFieldIds"
      @answers-updated="handleAnswersUpdated"
    />
  </QuestionPadding>
</template>
<script lang="ts">
import { computed, type DeepReadonly, defineComponent, type PropType } from 'vue'

import { type Answer, assertAnswerType } from '../../../answer/models/Answer'
import QuestionPadding from '../../../question/components/dumb/QuestionPadding.vue'
import { isSpecialFieldId } from '../../../question/models/Field'
import { type Question } from '../../../question/models/Question'
import ChoiceMultiple from '../dumb/ChoiceMultiple.vue'
import ChoiceSingle from '../dumb/ChoiceSingle.vue'

export default defineComponent({
  components: { QuestionPadding, ChoiceSingle, ChoiceMultiple },
  props: {
    question: {
      type: Object as PropType<
        DeepReadonly<Pick<Question<'choice'>, 'title' | 'fields' | 'maxAnswers'>>
      >,
      required: true,
    },
    answers: {
      type: Array as PropType<DeepReadonly<Answer[]>>,
      required: true,
    },
  },
  emits: {
    answersUpdated: (_newAnswers: Answer[]) => true,
  },
  setup(props, ctx) {
    const fields = computed(() =>
      props.question.fields.filter(field => field.type === 'choice' || field.type === 'choiceText'),
    )

    const selectedFieldIds = computed(() => {
      return new Set(
        props.answers
          .filter(
            answer =>
              (answer.type === 'choice' || answer.type === 'choiceText') && answer.value.selected,
          )
          .map(answer => answer.fieldId),
      )
    })

    const handleAnswersUpdated = (newAnswers: Answer[]): void => {
      ctx.emit(
        'answersUpdated',
        newAnswers.map(function (answer) {
          if (isSpecialFieldId(answer.fieldId)) {
            assertAnswerType(answer.type, 'choice')
            return { ...answer, value: { selected: false } }
          }
          return answer
        }),
      )
    }

    const ChoiceComponent = computed(() =>
      props.question.maxAnswers > 1 ? ChoiceMultiple : ChoiceSingle,
    )

    return {
      fields,
      selectedFieldIds,
      handleAnswersUpdated,
      ChoiceComponent,
    }
  },
})
</script>
